import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  FileText,
  Loader2,
  ScrollText,
  ShieldCheck,
} from 'lucide-react';
import { getApiClient, type LegalStaleDoc } from '@signalvote/api-client';
import { LegalAcceptanceSource, type LegalDocumentResponse, type LegalDocumentType } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

function isLegalStaleDoc(d: unknown): d is LegalStaleDoc {
  return (
    typeof d === 'object' && d !== null &&
    typeof (d as LegalStaleDoc).docType === 'string' &&
    typeof (d as LegalStaleDoc).requiredSequence === 'number'
  );
}

// TanStack Router auto-JSON-parses search params, so `stale=[{...}]` in the URL
// arrives here as an array — not a string. Handle both shapes defensively so
// downstream consumers always get a well-typed array (or [] on drift).
function normalizeStale(raw: unknown): LegalStaleDoc[] {
  if (Array.isArray(raw)) return raw.filter(isLegalStaleDoc);
  if (typeof raw === 'string' && raw.length > 0) {
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(isLegalStaleDoc) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export const Route = createFileRoute('/legal/accept')({
  component: LegalAcceptScreen,
  validateSearch: (search: Record<string, unknown>): { stale: LegalStaleDoc[] } => ({
    stale: normalizeStale(search.stale),
  }),
});

function LegalAcceptScreen() {
  const navigate = useNavigate();
  const { stale: staleDocs } = Route.useSearch();

  // `undefined` = loading; `null` = fetch failed; object = loaded.
  const [docBodies, setDocBodies] = useState<Record<string, LegalDocumentResponse | null>>({});
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<string | null>(null);

  const allAccepted = useMemo(
    () => staleDocs.length > 0 && staleDocs.every((d) => accepted[d.docType]),
    [staleDocs, accepted],
  );
  const acceptedCount = staleDocs.filter((d) => accepted[d.docType]).length;

  // Fetch each stale doc's body once. We deliberately do NOT navigate home when
  // staleDocs is empty — if `/` immediately re-triggers the wall (which it does
  // when the user has anything to accept), we'd loop back here. Let the user
  // click their way out via the fallback UI below.
  useEffect(() => {
    if (staleDocs.length === 0) return;
    let cancelled = false;
    (async () => {
      const { legalDocuments } = getApiClient();
      const entries = await Promise.all(
        staleDocs.map(async (d) => {
          try {
            const body = await legalDocuments.getDocument({
              docType: d.docType,
              sequence: d.requiredSequence,
            });
            return [d.docType, body] as const;
          } catch {
            return [d.docType, null] as const;
          }
        }),
      );
      if (cancelled) return;
      setDocBodies(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [staleDocs]);

  // When every stale doc has been accepted, get out of the way.
  useEffect(() => {
    if (allAccepted) {
      // Brief pause so users see the last "Accepted" confirmation before we navigate.
      const t = window.setTimeout(() => navigate({ to: '/', replace: true }), 700);
      return () => window.clearTimeout(t);
    }
  }, [allAccepted, navigate]);

  const onAccept = useCallback(async (doc: LegalStaleDoc) => {
    setSubmitting(doc.docType);
    setErrors((prev) => {
      const next = { ...prev };
      delete next[doc.docType];
      return next;
    });
    try {
      await getApiClient().legalAcceptance.accept({
        docType: doc.docType,
        sequence: doc.requiredSequence,
        source: LegalAcceptanceSource.Web,
      });
      setAccepted((prev) => ({ ...prev, [doc.docType]: true }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [doc.docType]: err instanceof Error ? err.message : 'Failed to accept — please retry.',
      }));
    } finally {
      setSubmitting(null);
    }
  }, []);

  // Fallback for /legal/accept opened with no stale docs (manual URL nav or
  // post-accept refresh before we could redirect).
  if (staleDocs.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
        <Card className="w-full max-w-md p-8 text-center">
          <div
            aria-hidden
            className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary"
          >
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            You&rsquo;re all caught up
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            No legal documents need your attention right now.
          </p>
          <Button
            type="button"
            className="mt-6 w-full"
            onClick={() => navigate({ to: '/', replace: true })}
          >
            Continue to app
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-start sm:items-center justify-center px-6 py-10 sm:py-16">
      <div className="w-full max-w-2xl">
        <header className="mb-8">
          <div className="mb-4 flex items-center gap-3">
            <div
              aria-hidden
              className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary"
            >
              <ShieldCheck className="h-5 w-5" />
            </div>
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Action required
            </span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Updated legal terms
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Before continuing, please review and accept the following{' '}
            {staleDocs.length === 1 ? 'document' : `${staleDocs.length} documents`}.
            {staleDocs.length > 1 && (
              <>
                {' '}
                <span className="text-foreground/70">
                  ({acceptedCount} of {staleDocs.length} accepted)
                </span>
              </>
            )}
          </p>
        </header>

        <div className="space-y-4">
          {staleDocs.map((doc) => {
            const loaded = docBodies[doc.docType];
            const isAccepted = accepted[doc.docType] === true;
            const isSubmitting = submitting === doc.docType;
            const err = errors[doc.docType];
            const Icon = iconForDocType(doc.docType);

            return (
              <Card
                key={doc.docType}
                className="overflow-hidden"
                aria-labelledby={`doc-${doc.docType}-title`}
              >
                <div className="flex items-center gap-3 border-b border-border p-4">
                  <div
                    aria-hidden
                    className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground"
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2
                      id={`doc-${doc.docType}-title`}
                      className="text-[15px] font-medium text-foreground"
                    >
                      {docTypeLabel(doc.docType)}
                    </h2>
                  </div>
                  {isAccepted && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                      <Check className="h-3.5 w-3.5" aria-hidden />
                      Accepted
                    </span>
                  )}
                </div>

                <div className="p-4">
                  {loaded === undefined && (
                    <div className="space-y-2" aria-busy="true" aria-label="Loading document">
                      <Skeleton className="h-3 w-full" />
                      <Skeleton className="h-3 w-11/12" />
                      <Skeleton className="h-3 w-4/5" />
                      <Skeleton className="h-3 w-2/3" />
                    </div>
                  )}
                  {loaded === null && (
                    <p className="text-sm text-destructive">
                      Could not load this document. Please refresh and try again.
                    </p>
                  )}
                  {loaded && (
                    <div className="max-h-80 overflow-auto rounded-md border border-border/60 bg-muted/30 p-4">
                      <div className="whitespace-pre-line text-sm leading-relaxed text-foreground/90">
                        {loaded.body}
                      </div>
                    </div>
                  )}

                  {err && (
                    <p className="mt-3 text-sm text-destructive" role="alert">
                      {err}
                    </p>
                  )}

                  <div className="mt-4 flex items-center justify-end">
                    <Button
                      type="button"
                      onClick={() => onAccept(doc)}
                      disabled={isAccepted || isSubmitting || !loaded}
                      className="min-w-[9rem]"
                    >
                      {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
                      {isAccepted ? 'Accepted' : isSubmitting ? 'Accepting…' : 'I Agree'}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {allAccepted && (
          <p className="mt-6 text-center text-sm text-muted-foreground" role="status">
            Thanks — taking you back to the app…
          </p>
        )}
      </div>
    </div>
  );
}

type KnownDocType = LegalDocumentType;

// Typing the map as Record<KnownDocType, ...> means a new C# LegalDocumentType member
// is a compile error until every map here gets a case. Unknown docTypes returned by
// the backend fall through to a generic label + icon so the wall keeps working — a
// missing entry can't crash the accept screen the way the old assertNever cast could.
const DOC_TYPE_LABELS: Record<KnownDocType, string> = {
  Eula: 'End User License Agreement',
  PrivacyPolicy: 'Privacy Policy',
  TermsOfService: 'Terms of Service',
};

const DOC_TYPE_ICONS: Record<KnownDocType, typeof FileText> = {
  Eula: FileText,
  PrivacyPolicy: ShieldCheck,
  TermsOfService: ScrollText,
};

function docTypeLabel(t: string): string {
  return (DOC_TYPE_LABELS as Record<string, string | undefined>)[t] ?? 'Legal Document';
}

function iconForDocType(t: string) {
  return (DOC_TYPE_ICONS as Record<string, typeof FileText | undefined>)[t] ?? FileText;
}
